import { Film, Image } from 'lucide-react';
import { resolveMustHaves, resolveProjectCategory, MUST_HAVE_CATEGORY_LABELS } from '@/lib/mustHaves';
import { SAMPLE_KINDS, sampleLinks, samplesFor } from '@/lib/samples';
import { DOC_EDIT_NOTES_LABEL } from '@/lib/docEditNotes';
import { EditingGuideValue } from '@/components/EditingGuide';

/**
 * A client's Style Profile as they wrote it, read-only: the project drawer's Profile tab, "See what the client
 * wrote in their Style Profile" in the Notes section (the drawer's and the Redemption Cut page's), and the Style
 * Profile shown in place of the AI notes when there are none. `showOnly` limits it to the project's kind (video or
 * photo); editors (`isEditor`) see the fields in the order they work in, and with `showMustHaves` the client's
 * must-haves as written; `excludeAssets` leaves out the assets link. With a `project`, the sample videos are that
 * kind of video's (lib/samples.js), saying so when they are the highlight film's.
 */
export function StyleProfileView({ profile, editUrl, showOnly, excludeAssets, isEditor, showMustHaves, projectType, project }) {
  if (profile === undefined) return <p className="text-[11px] text-slate-600 p-5" data-testid="style-profile-loading">Loading style profile…</p>;
  if (!profile || !profile.profile_type) return <p className="text-[11px] text-slate-600 p-5">No style profile set up</p>;
  const v = profile.video || {};
  const p = profile.photo || {};
  const showVideo = showOnly ? showOnly === 'video' : (profile.profile_type === 'video' || profile.profile_type === 'both');
  const showPhoto = showOnly ? showOnly === 'photo' : (profile.profile_type === 'photo' || profile.profile_type === 'both');
  // Short row: label left, value right (for short values like NLE, FPS, Resolution)
  const Row = ({ label, value, link }) => value ? (
    <div className="flex justify-between items-start py-1 border-b border-white/[0.03] gap-3 min-w-0 max-w-full">
      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-ug-gold/50 shrink-0 mt-0.5 w-20">{label}</span>
      {link && value.startsWith?.('http') ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 text-right min-w-0 break-all truncate">{value}</a>
      ) : (
        <span className="text-[10px] text-zinc-100/80 text-right min-w-0 break-all">{value}</span>
      )}
    </div>
  ) : null;
  // Long row: label on top, value below (for paragraphs, long notes); `node` renders in place of the text, `note` under the label
  const LongRow = ({ label, value, link, node, note, testId }) => value ? (
    <div className="py-2 border-b border-white/[0.03] min-w-0 overflow-hidden max-w-full" data-testid={testId}>
      <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-ug-gold/50 block mb-1">{label}</span>
      {note && <p className="text-[10px] text-slate-400 leading-relaxed mb-1" data-testid={testId && `${testId}-note`}>{note}</p>}
      {node ? (
        <p className="text-[10px] text-zinc-100/70 leading-relaxed">{node}</p>
      ) : link ? (
        <div className="space-y-0.5">
          {String(value).split('\n').filter(Boolean).map((v, i) => (
            <a key={i} href={v.trim()} target="_blank" rel="noopener noreferrer" className="block text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 break-all truncate">{v.trim()}</a>
          ))}
        </div>
      ) : (
        <p className="text-[10px] text-zinc-100/70 leading-relaxed whitespace-pre-wrap break-all">{value}</p>
      )}
    </div>
  ) : null;
  // For "both" profiles: show the project-relevant section first
  const photoFirst = projectType === 'photo' && showVideo && showPhoto;

  const videoSection = showVideo && (
    <div>
      <h4 className="font-sans text-[11px] font-medium uppercase tracking-wider text-ug-gold flex items-center gap-2 mb-3">
        <Film className="w-4 h-4" /> Video Style Profile{editUrl ? <span className="text-slate-500 text-[10px] ml-1">(<a href={editUrl} className="hover:text-ug-gold transition-colors underline">edit</a>)</span> : null}
      </h4>
      <div className="bg-ug-surface/50 rounded-xl border border-ug-border p-3 overflow-hidden">
        {/* Client Must-Haves — read-only display for admin/staff/client. Editors
            work the INTERACTIVE checklist in the Review tab; in the Notes
            section (showMustHaves) they see this list too, as the client wrote it.
            2026-07-15 three-way split: reads the CATEGORY that matches this
            project's resolved edit style. Backward-compat alias: legacy flat
            `must_haves[]` displays under Highlight-mapped projects. */}
        {(() => {
          if (isEditor && !showMustHaves) return null;
          // Shared both-shape resolver (lib/mustHaves) — never throws on
          // legacy flat arrays, missing categories, or absent project prop.
          const { category, items } = resolveMustHaves(v, project);
          if (!items.length) return null;
          return (
            <div className="tap-auto py-2 border-b border-white/[0.03]" data-testid="drawer-must-haves">
              <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-ug-gold/50 block mb-1.5">
                Client Must-Haves — {MUST_HAVE_CATEGORY_LABELS[category]}
              </span>
              <div className="space-y-1">
                {items.map((m, i) => (
                  <div key={m.id || i} className="tap-auto flex items-start gap-1.5" data-testid={`drawer-must-have-${i}`}>
                    <span className="w-2.5 h-2.5 rounded-[3px] border border-ug-gold/40 shrink-0 mt-[3px]" />
                    <span className="text-[10px] text-zinc-100/80 leading-relaxed break-words">{m.text}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {project ? (() => {
          const s = samplesFor(v, resolveProjectCategory(project));
          return <LongRow label={s.kind.label} value={s.links.join('\n')} note={s.fallback} link testId="profile-samples" />;
        })() : SAMPLE_KINDS.map(k => (
          <LongRow key={k.field} label={k.label} value={sampleLinks(v, k.field).join('\n')} link testId={`profile-samples-${k.testid}`} />
        ))}
        {v.cameras?.length > 0 && <Row label="Cameras" value={v.cameras.map(c => `${c.brand} ${c.model} ${c.profile || ''}`.trim()).filter(Boolean).join(', ')} />}
        <Row label="NLE" value={v.nle_software === 'Other' ? v.nle_software_other : v.nle_software} />
        <Row label="Codec" value={v.codec === 'Other' ? v.codec_other : v.codec} />
        <Row label="FPS" value={v.fps === 'Other' ? v.fps_other : v.fps} />
        <Row label="Resolution" value={v.resolution === 'Other' ? v.resolution_other : v.resolution} />
        <Row label="Compression" value={v.compression === 'Other' ? v.compression_other : v.compression} />
        <Row label="Font" value={[v.font_name, v.font_size && `${v.font_size}px`, v.font_spacing && `spacing: ${v.font_spacing}`].filter(Boolean).join(', ')} />
        <Row label="LUT %" value={v.lut_percentage} />
        {!isEditor && <Row label="Language" value={v.client_language} />}
        {isEditor ? (
          <>
            <LongRow label="LUT Notes" value={v.lut_notes} />
            <LongRow label="Color Grading" value={v.color_grading} />
            <LongRow label="Story Telling" value={v.story_telling} />
            <LongRow label="Editing Guide" value={v.editing_guide} node={<EditingGuideValue video={v} />} />
            <Row label="Music Source" value={v.music_source} />
            <LongRow label="Music Notes" value={v.music_notes} />
            <LongRow label="Additional Notes" value={v.additional_notes} />
            {/* the client's notes for doc edits: an editor sees them on a doc edit (they feed nothing else) */}
            {(!project || resolveProjectCategory(project) === 'doc_edit') &&
              <LongRow label={DOC_EDIT_NOTES_LABEL} value={v.doc_edit_notes} testId="profile-doc-edit-notes" />}
          </>
        ) : (
          <>
            <LongRow label="Color Grading" value={v.color_grading} />
            <LongRow label="Story Telling" value={v.story_telling} />
            <LongRow label="Editing Guide" value={v.editing_guide} node={<EditingGuideValue video={v} />} />
            {!excludeAssets && <LongRow label="Assets Link" value={v.assets_link} link />}
            <LongRow label="Music Notes" value={v.music_notes} />
            <Row label="Music Source" value={v.music_source} />
            <LongRow label="LUT Notes" value={v.lut_notes} />
            <LongRow label="Notes" value={v.additional_notes} />
            <LongRow label={DOC_EDIT_NOTES_LABEL} value={v.doc_edit_notes} testId="profile-doc-edit-notes" />
          </>
        )}
      </div>
    </div>
  );

  const photoSection = showPhoto && (
    <div>
      <h4 className="font-sans text-[11px] font-medium uppercase tracking-wider text-ug-gold flex items-center gap-2 mb-3">
        <Image className="w-4 h-4" /> Photo Style Profile{editUrl ? <span className="text-slate-500 text-[10px] ml-1">(<a href={editUrl} className="hover:text-ug-gold transition-colors underline">edit</a>)</span> : null}
      </h4>
      <div className="bg-ug-surface/50 rounded-xl border border-ug-border p-3 overflow-hidden">
        {(() => {
          const presets = (Array.isArray(p.presets) && p.presets.length)
            ? p.presets
            : ((p.preset_filename || p.preset_file_id) ? [{ file_id: p.preset_file_id, filename: p.preset_filename }] : []);
          if (!presets.length) return null;
          return (
            <div className="flex justify-between items-start py-1 border-b border-white/[0.03] gap-3 min-w-0 max-w-full">
              <span className="font-sans text-[8px] font-medium uppercase tracking-wider text-ug-gold/50 shrink-0 mt-0.5 w-20">{presets.length > 1 ? 'Presets' : 'Preset'}</span>
              <div className="flex flex-col items-end gap-1 min-w-0">
                {presets.map((pr, i) => (
                  pr.file_id ? (
                    <a key={pr.file_id || i} href={`${process.env.REACT_APP_BACKEND_URL}/api/storage/serve/${pr.file_id}`} className="text-[10px] text-oxidized-teal hover:text-oxidized-teal/80 underline underline-offset-2 text-right min-w-0 break-all" data-testid={i === 0 ? 'preset-download-link' : `preset-download-link-${i}`}>{pr.filename}</a>
                  ) : (
                    <span key={i} className="text-[10px] text-zinc-100/80 text-right min-w-0 break-all">{pr.filename}</span>
                  )
                ))}
              </div>
            </div>
          );
        })()}
        {p.avoid_magenta && (
          <div className="py-1 border-b border-white/[0.03]">
            <span className="font-sans text-[9px] font-medium uppercase tracking-wider text-oxidized-teal/70">&#10003; Avoid Magenta</span>
          </div>
        )}
        {p.avoid_green && (
          <div className="py-1 border-b border-white/[0.03]">
            <span className="font-sans text-[9px] font-medium uppercase tracking-wider text-oxidized-teal/70">&#10003; Avoid Green</span>
          </div>
        )}
        <LongRow label="White Balance" value={p.white_balance} />
        <LongRow label="Exposure" value={p.exposure_contrast} />
        <LongRow label="Skin Tones" value={p.skin_tone_bias} />
        <LongRow label="B&W Usage" value={p.bw_usage} />
        <LongRow label="Crop Rules" value={p.crop_straighten} />
        <LongRow label="Bursts" value={p.burst_duplicates} />
        <LongRow label="Culling" value={p.culling_notes} />
        <LongRow label="Editor Notes" value={p.editor_notes} />
        <LongRow label="Ref Galleries" value={p.reference_galleries?.filter(Boolean).join('\n')} link />
        {p.internal_notes && <LongRow label="Internal" value={p.internal_notes} />}
      </div>
    </div>
  );

  return (
    <div className="space-y-4 w-full overflow-hidden">
      {photoFirst ? <>{photoSection}{videoSection}</> : <>{videoSection}{photoSection}</>}
    </div>
  );
}
